import type { SearchSuggestion } from '@/api/v4/search.api';
import { useEnrichedCompanySuggestions } from './use-enriched-company-suggestions.query';
import { useMemo } from 'react';
import type { EnrichedCompanySearchSuggestion } from '@/api/v4/organization-enrich.api';
import { useCompanySuggestionsTableConfig } from './use-company-suggestions-table-config';
import { TableHeaderCell } from '@/ui/table/infinite-table/table-header-cell';
import { styled } from 'goober';
import { ScrollableTableContainer } from '../shared/scrollable-table-container';
import { TableBody } from '@/ui/table/infinite-table/table-body';
import { colors } from '@/theme/colors';
import { combineCompanyData } from './combine-company-data';
import { Button } from '@/ui/button/button';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { useTranslation } from 'react-i18next';

export type CompanySearchSuggestion = {
  basicData: SearchSuggestion;
  enrichedData: EnrichedCompanySearchSuggestion;
};

export const CompanySuggestionsTable = ({
  data,
  hasNextPage,
  onShowMore,
}: {
  data: SearchSuggestion[];
  hasNextPage?: boolean;
  onShowMore?: () => void;
}) => {
  const { t } = useTranslation('default', { keyPrefix: 'globalSearch' });
  const searchSuggestionsCompanyIds = data.map(suggestion => suggestion.id);
  const { data: enrichedCompanySuggestions, isLoading } =
    useEnrichedCompanySuggestions(searchSuggestionsCompanyIds);

  const combinedCompaniesData = useMemo(
    () => combineCompanyData(data, enrichedCompanySuggestions),
    [data, enrichedCompanySuggestions],
  );

  const { rows, headers } = useCompanySuggestionsTableConfig(
    combinedCompaniesData,
    isLoading,
  );

  if (!isLoading && !combinedCompaniesData.length) {
    return null;
  }

  return (
    <div>
      <ScrollableTableContainer>
        <Table>
          <thead>
            <tr>
              {headers.cells.map(cell => (
                <TableHeaderCell key={cell.columnKey} cell={cell} />
              ))}
            </tr>
          </thead>
          <TableBody
            id="company-enriched-suggestions-table-body"
            rows={rows}
            isLoading={isLoading}
            borderColor={colors.gray.c3}
          />
          {!isLoading && hasNextPage ? (
            <tfoot>
              <tr>
                <td colSpan={headers.cells.length}>
                  <Flexbox justify="center" margin="12px">
                    <Button variant="secondary" onClick={onShowMore}>
                      {t('showMore')}
                    </Button>
                  </Flexbox>
                </td>
              </tr>
            </tfoot>
          ) : undefined}
        </Table>
      </ScrollableTableContainer>
    </div>
  );
};

const Table = styled('table')`
  ${({ theme }) => theme.mixins.scrollbar};
  border-collapse: separate;
  overflow: auto;
  display: table;
  max-height: 100%;
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadow.suggestionsTable};
  width: 100%;

  tr:not(:first-child) {
    box-shadow: ${({ theme }) => theme.shadow.suggestionsTableRow};
  }
`;

// Uses shared ScrollableTableContainer
